import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HeaderMenu from './HeaderMenu';
import UserMenu from './UserMenu';
import LabAccessDialog from './LabAccessDialog';
import '../css/Header.css';
import '../css/HeaderMenu.css';
import '../css/UserMenu.css';
import '../css/PageTemplate.css';
import { useAuth } from '../contexts/AuthContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import awsExports from '../config/aws-exports';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faBars } from '@fortawesome/free-solid-svg-icons';

const Header = () => {
  const { isAuthenticated, userLabel, authenticateUser } = useAuth();
  const [sandboxSignInUrl, setSandboxSignInUrl] = useState('');
  // Account id, LabUser and the class's initial password, for the sign-in panel.
  // Null whenever the backend has not been updated yet, which leaves the menu
  // exactly as it was rather than rendering an empty panel.
  const [lab, setLab] = useState(null);
  // E9-S16. Every active class the student holds an account in, running first - the
  // backend's order. `lab` stays the first of them, for the menus.
  const [labs, setLabs] = useState([]);
  // Owned here, not in the menu: the menu unmounts when it closes, and the
  // dialog has to outlive that.
  const [labDialogOpen, setLabDialogOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const isUserMenuOpenRef = useRef(isUserMenuOpen);
  const isHeaderMenuOpenRef = useRef(isHeaderMenuOpen);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // E9-S18. Describe or start the student's IDE host, through the sandbox API.
  const labInstance = useCallback(async (accountId, action) => {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    const response = await fetch(`${awsExports.GET_SANDBOX_API_URL}sandbox/instance`, {
      method: 'POST',
      headers: { Authorization: idToken.toString(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountId, action }),
    });
    if (!response.ok) throw new Error(`The lab machine could not be reached (${response.status})`);
    return response.json();
  }, []);

  async function getSandboxAccountForUser() {
    if (localStorage.getItem('isAuthenticated') === 'false' || localStorage.getItem('isAuthenticated') === null) return;
    const apiUrl = awsExports.GET_SANDBOX_API_URL;
    const fullUrl = `${apiUrl}sandbox`;
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: { Authorization: idToken.toString() },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      if (response.status === 204) {
        setSandboxSignInUrl('');
        setLab(null);
        setLabs([]);
        return;
      }
      const data = await response.json();
      setSandboxSignInUrl(`https://${data.accounts[0]}.signin.aws.amazon.com/console`);
      setLab(data.labs && data.labs.length > 0 ? data.labs[0] : null);
      setLabs(data.labs || []);
    } catch (error) {
      setSandboxSignInUrl('');
      setLab(null);
      setLabs([]);
      console.error('There has been a problem with sandbox account information retrieval:', error);
    }
  }

  useEffect(() => {
    function base64UrlDecode(input) {
      let base64 = input.replace(/-/g, '+').replace(/_/g, '/');
      switch (base64.length % 4) {
        case 0:
          break;
        case 2:
          base64 += '==';
          break;
        case 3:
          base64 += '=';
          break;
        default:
          throw new Error('Illegal base64url string!');
      }
      const raw = window.atob(base64);
      return decodeURIComponent(
        raw
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );
    }

    function decodeJWT(token) {
      const parts = token.split('.');
      if (parts.length !== 3) throw new Error('The token is invalid');
      const header = JSON.parse(base64UrlDecode(parts[0]));
      const payload = JSON.parse(base64UrlDecode(parts[1]));
      return { header, payload };
    }

    async function decodeAndAuthenticate() {
      try {
        const session = await fetchAuthSession();
        if (!session || !session.tokens || !session.tokens.idToken) {
          authenticateUser(false, '', '');
          return;
        }
        const idToken = session.tokens.idToken.toString();
        const decodedToken = decodeJWT(idToken);
        const username = userLabel ? userLabel : decodedToken.payload.name;
        const email = decodedToken.payload.email;
        authenticateUser(true, username, email);
      } catch (error) {
        console.error('Error checking auth status:', error);
        authenticateUser(false, '', '');
      }
    }

    decodeAndAuthenticate();
    getSandboxAccountForUser();
  }, [isAuthenticated, authenticateUser, userLabel]);

  useEffect(() => {
    isUserMenuOpenRef.current = isUserMenuOpen;
    isHeaderMenuOpenRef.current = isHeaderMenuOpen;

    if (isUserMenuOpen || isHeaderMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserMenuOpen, isHeaderMenuOpen]);

  const handleClickOutside = (event) => {
    const userIcon = document.getElementById('user-icon');
    const hamburger = document.getElementById('hamburger');
    if (userIcon?.contains(event.target) || hamburger?.contains(event.target)) return;
    // A click *inside* the menu is not a click outside it. Every item used to be a link
    // that navigated away, so closing on it was invisible; the first control that only
    // changes state in place - the AWS Sandbox panel - made it look broken instead,
    // because the menu unmounted in the same tick that opened the panel.
    const userMenu = document.getElementById('user-menu');
    const headerMenu = document.getElementById('menu');
    if (userMenu?.contains(event.target) || headerMenu?.contains(event.target)) return;
    if (isUserMenuOpenRef.current || isHeaderMenuOpenRef.current) {
      setIsUserMenuOpen(false);
      setIsHeaderMenuOpen(false);
    }
  };

  // Opening the dialog closes the menu behind it. The dialog's own state lives here, so
  // it survives that - which is the whole reason it is not owned by the menu.
  const openLabAccess = () => {
    setLabDialogOpen(true);
    setIsUserMenuOpen(false);
    setIsHeaderMenuOpen(false);
  };

  const toggleUserMenu = () => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    setIsUserMenuOpen(!isUserMenuOpen);
    getSandboxAccountForUser();
  };

  const toggleHeaderMenu = () => {
    setIsHeaderMenuOpen(!isHeaderMenuOpen);
  };

  const handleOpenChange = (newOpenState) => {
    setIsHeaderMenuOpen(newOpenState);
  };

  return (
    <header id="siteHeader" className="site-header">
      <div className="header-left">
        <div>
          <span className="logo-syntax">Syntax</span>
          <span className="logo-nebula">Nebula</span>
        </div>
        <div className="logo-text">Cloud Training and Consulting</div>
        <Link to="/">
          <img className="logo" src="/images/logo_small.webp" alt="Logo" />
        </Link>
      </div>
      <div className="header-right">
        {!isMobile && (
          <button onClick={toggleUserMenu} className={`user-icon ${isUserMenuOpen ? 'active' : ''}`} id="user-icon">
            <FontAwesomeIcon icon={faUser} />
            <span className="menu-label">
              {isAuthenticated
                ? userLabel
                  ? userLabel
                      .split(' ')
                      .map((word) => word[0])
                      .join('')
                      .toUpperCase()
                  : 'User'
                : 'Sign In'}
            </span>
          </button>
        )}
        {!isMobile && isUserMenuOpen && (
          <UserMenu
            isAuthenticated={isAuthenticated}
            sandboxSignInUrl={sandboxSignInUrl}
            lab={lab}
            onOpenLabAccess={openLabAccess}
          />
        )}
        <div onClick={toggleHeaderMenu} className={`hamburger ${isHeaderMenuOpen ? 'active' : ''}`} id="hamburger">
          <FontAwesomeIcon icon={faBars} />
        </div>
        <HeaderMenu
          open={isHeaderMenuOpen}
          isAuthenticated={isAuthenticated}
          sandboxSignInUrl={sandboxSignInUrl}
          lab={lab}
          onOpenLabAccess={openLabAccess}
          onOpenChange={handleOpenChange}
        />
      </div>

      <LabAccessDialog
        open={labDialogOpen}
        lab={lab}
        labs={labs}
        labInstance={labInstance}
        sandboxSignInUrl={sandboxSignInUrl}
        onClose={() => setLabDialogOpen(false)}
      />
    </header>
  );
};

export default Header;
